import { useCallback, useRef, useState } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { MODEL_FEEDBACK_SCORES as SCORES } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import { markFeedbackSubmitted } from 'Pods/ThreeDStudio/Store/actions/ui'

import getCommentError, { normalizeComment } from './Utils/getCommentError'

const MAX_SCORE = SCORES[SCORES.length - 1]
const INITIAL_SCORES = { matchesInput: null, geometry: null, textures: null }

const showNotification = (message, type) => {
  if (typeof window !== 'undefined' && window.showNotification) {
    window.showNotification(message, { type, autohide: true })
  }
}

// The free generation is granted for a complete form only: every shown criterion scored
// and, unless every score is the maximum, a comment saying what fell short. A criterion
// the form does not show (textures on a non-PBR model) is left out of the payload. The
// comment's rules are only flagged once the user tries to submit without meeting them.
export default function useFeedbackForm({ runId, criteria }) {
  const t = useTranslation()
  const { postData, isLoading } = useApiPost()
  const [scores, setScores] = useState(INITIAL_SCORES)
  const [comment, setComment] = useState('')
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false)
  const hasStartedRef = useRef(false)

  const setScore = useCallback((criterion, score) => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true
      trackStudioEvent(STUDIO_EVENT.FEEDBACK_START, { run_id: runId })
    }
    setScores(prev => ({ ...prev, [criterion]: score }))
  }, [runId])

  const handleCommentChange = useCallback(event => setComment(event.target.value), [])

  const isComplete = criteria.every(criterion => scores[criterion] != null)
  const isCommentRequired = !criteria.every(criterion => scores[criterion] === MAX_SCORE)
  const commentError = isCommentRequired ? getCommentError(comment) : null

  const submit = useCallback((event) => {
    event.preventDefault()
    if (!isComplete || isLoading) return
    if (commentError) {
      setHasTriedSubmit(true)
      return
    }

    postData({
      path: `/api/internal/generator/runs/${runId}/feedback`,
      data: {
        feedback: {
          matches_input: scores.matchesInput,
          geometry: scores.geometry,
          ...(criteria.includes('textures') && { textures: scores.textures }),
          comment: normalizeComment(comment),
        },
      },
      onSuccess: () => {
        trackStudioEvent(STUDIO_EVENT.FEEDBACK_SUBMIT, { run_id: runId })
        markFeedbackSubmitted()
        // One sentence per line, as agreed in the design thread, instead of wrapping
        // mid-sentence at the toast's max width.
        showNotification(
          `${t('viewer.modelFeedback.successTitle')}<br>${t('viewer.modelFeedback.successDescription')}`,
          'success',
        )
      },
      // The 422 body carries a translated reason (comment too short, already submitted, daily cap).
      onError: (error) => {
        showNotification(error?.response?.data?.errors?.[0] || t('viewer.modelFeedback.error'), 'error')
      },
    })
  }, [isComplete, commentError, isLoading, postData, runId, criteria, scores, comment, t])

  return {
    scores,
    setScore,
    comment,
    handleCommentChange,
    isCommentRequired,
    commentError: hasTriedSubmit ? commentError : null,
    canSubmit: isComplete,
    isLoading,
    submit,
  }
}
